import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../hooks/useAuth';
import { useClub } from '../../hooks/useClub';

function getTrialDay(d) {
  const s=new Date(d); s.setHours(0,0,0,0);
  const t=new Date(); t.setHours(0,0,0,0);
  return Math.floor((t-s)/86400000)+1;
}
function getDaysLeft(endDate) {
  if (!endDate) return 0;
  const end=new Date(endDate); end.setHours(0,0,0,0);
  const today=new Date(); today.setHours(0,0,0,0);
  return Math.max(0, Math.round((end-today)/86400000));
}
function isLastVisitStale(d) { if(!d) return true; return (new Date()-new Date(d))/86400000>4; }
function fmtDate(d) { if(!d) return '—'; return new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric'}); }
function getInitials(n) { if(!n) return '??'; return n.split(' ').map(p=>p[0]).join('').toUpperCase().slice(0,2); }
function avatarColor(n) {
  const c=['#7c3aed','#2563eb','#059669','#d97706','#dc2626','#0891b2'];
  let h=0; for(let i=0;i<(n||'').length;i++) h=(h*31+n.charCodeAt(i))&0xffff;
  return c[h%c.length];
}

function Avatar({ name, size=36 }) {
  return <div style={{width:size,height:size,borderRadius:'50%',background:avatarColor(name),display:'flex',alignItems:'center',justifyContent:'center',fontWeight:700,fontSize:size*0.36,color:'#fff',flexShrink:0}}>{getInitials(name)}</div>;
}
function SectionHeader({ color, icon, title, subtitle, count, badge }) {
  return (
    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:10,paddingBottom:8,borderBottom:`1px solid ${color}40`}}>
      <span style={{fontSize:18}}>{icon}</span>
      <div style={{flex:1}}>
        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
          <span style={{fontFamily:'DM Sans,sans-serif',fontWeight:700,fontSize:13,color,letterSpacing:'0.05em',textTransform:'uppercase'}}>{title}</span>
          {count!==undefined && <span style={{background:color,color:'#000',borderRadius:10,padding:'1px 8px',fontSize:11,fontWeight:700}}>{count}</span>}
          {badge && <span style={{background:color+'22',color,border:`1px solid ${color}`,borderRadius:4,padding:'1px 6px',fontSize:10,fontWeight:700}}>{badge}</span>}
        </div>
        {subtitle && <div style={{fontSize:11,color:'#777',marginTop:2}}>{subtitle}</div>}
      </div>
    </div>
  );
}
function DataField({ label, value, color='#aaa' }) {
  return (
    <div>
      <div style={{fontSize:10,color:'#555',textTransform:'uppercase',letterSpacing:'0.05em'}}>{label}</div>
      <div style={{fontSize:13,fontWeight:600,color,fontFamily:'DM Mono,monospace'}}>{value}</div>
    </div>
  );
}
function KPIBar({ data }) {
  const items=[
    {label:'At Risk',           value:data.atRisk??'—',           color:'#ef4444'},
    {label:'Connection Needed', value:data.connectionNeeded??'—', color:'#eab308'},
    {label:'Day 9+ Approaching',value:data.day9Plus??'—',         color:'#f97316'},
    {label:'Connected Today',   value:data.connectedToday??'—',   color:'#22c55e'},
    {label:'Active Trials',     value:data.activeTrials??'—',     color:'#e5e5e5'},
  ];
  return (
    <div style={{display:'flex',gap:12,flexWrap:'wrap',marginBottom:20}}>
      {items.map(item=>(
        <div key={item.label} style={{background:'#111',border:'1px solid #222',borderRadius:8,padding:'8px 14px',textAlign:'center',minWidth:80}}>
          <div style={{fontSize:22,fontWeight:700,color:item.color,fontFamily:'DM Mono,monospace'}}>{item.value}</div>
          <div style={{fontSize:10,color:'#666',textTransform:'uppercase',letterSpacing:'0.05em',whiteSpace:'nowrap'}}>{item.label}</div>
        </div>
      ))}
    </div>
  );
}

function ManagerCard({ trial, canEdit, onLogConnection, connectedToday }) {
  const [showForm, setShowForm] = useState(false);
  const [notes, setNotes] = useState('');
  const [connectedBy, setConnectedBy] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(connectedToday);
  const day=trial.trial_day || getTrialDay(trial.trial_start_date);
  const daysLeft=getDaysLeft(trial.trial_end_date);
  const staleVisit=isLastVisitStale(trial.last_visit_date);

  async function handleSubmit() {
    if(!connectedBy) return;
    setSubmitting(true); await onLogConnection(trial,connectedBy,notes);
    setSubmitting(false); setDone(true); setShowForm(false);
  }

  return (
    <div style={{background:'#111',border:`1px solid ${trial.at_risk?'#ef4444':'#2a2a2a'}`,borderRadius:8,marginBottom:10,overflow:'hidden',boxShadow:trial.at_risk?'0 0 0 1px #ef444430':'none'}}>
      <div style={{padding:'12px 16px',display:'flex',alignItems:'flex-start',gap:14}}>
        <Avatar name={trial.full_name} size={40} />
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:4}}>
            <span style={{fontWeight:700,fontSize:15,color:'#e5e5e5'}}>{trial.full_name}</span>
            {trial.at_risk && <span style={{background:'#ef444422',color:'#ef4444',border:'1px solid #ef4444',borderRadius:4,padding:'1px 6px',fontSize:10,fontWeight:700}}>AT RISK</span>}
            {!trial.manager_connection && <span style={{background:'#eab30822',color:'#eab308',border:'1px solid #eab308',borderRadius:4,padding:'1px 6px',fontSize:10,fontWeight:700}}>NO MGR. CONNECTION YET</span>}
            {done && <span style={{background:'#22c55e22',color:'#22c55e',border:'1px solid #22c55e',borderRadius:4,padding:'1px 6px',fontSize:10,fontWeight:700}}>✓ CONNECTED TODAY</span>}
          </div>
          <div style={{fontSize:12,color:'#888',marginBottom:8}}>{trial.email} · {trial.phone}</div>
          <div style={{display:'flex',gap:20,flexWrap:'wrap'}}>
            <DataField label="Trial Day" value={`Day ${day}`} />
            <DataField label="Total Visits" value={trial.total_visits??0} color={(trial.total_visits||0)>=2?'#22c55e':'#ef4444'} />
            <DataField label="Last Visit" value={fmtDate(trial.last_visit_date)} color={staleVisit?'#ef4444':'#e5e5e5'} />
            <DataField label="Source" value={trial.trial_source||'—'} />
            <DataField label="Sales Status" value={trial.next_action||trial.trial_status||'—'} />
            <DataField label="Days Left" value={`${daysLeft}d`} color={daysLeft<=3?'#ef4444':daysLeft<=7?'#eab308':'#22c55e'} />
          </div>
        </div>
        {canEdit && !done && (
          <button onClick={()=>setShowForm(f=>!f)} style={{background:showForm?'#333':'#22c55e',color:showForm?'#aaa':'#000',border:'none',borderRadius:6,padding:'8px 16px',fontSize:12,fontWeight:700,cursor:'pointer',flexShrink:0,whiteSpace:'nowrap'}}>
            {showForm?'✕ Cancel':'🤝 Log Connection'}
          </button>
        )}
        {!canEdit && <span style={{fontSize:11,color:'#555',flexShrink:0,paddingTop:4}}>View only</span>}
      </div>
      {showForm && canEdit && (
        <div style={{padding:'0 16px 16px',borderTop:'1px solid #222'}}>
          <div style={{marginTop:12,display:'flex',gap:12,flexWrap:'wrap',alignItems:'flex-end'}}>
            <div style={{flex:1,minWidth:180}}>
              <label style={{fontSize:11,color:'#888',display:'block',marginBottom:4}}>Manager Name *</label>
              <input value={connectedBy} onChange={e=>setConnectedBy(e.target.value)} placeholder="Your name" style={{width:'100%',background:'#1a1a1a',color:'#e5e5e5',border:'1px solid #333',borderRadius:6,padding:'6px 10px',fontSize:13,boxSizing:'border-box'}} />
            </div>
            <div style={{flex:2,minWidth:200}}>
              <label style={{fontSize:11,color:'#888',display:'block',marginBottom:4}}>Notes</label>
              <input value={notes} onChange={e=>setNotes(e.target.value)} placeholder="How did it go?" style={{width:'100%',background:'#1a1a1a',color:'#e5e5e5',border:'1px solid #333',borderRadius:6,padding:'6px 10px',fontSize:13,boxSizing:'border-box'}} />
            </div>
            <button onClick={handleSubmit} disabled={!connectedBy||submitting} style={{background:connectedBy?'#22c55e':'#333',color:connectedBy?'#000':'#666',border:'none',borderRadius:6,padding:'8px 20px',fontSize:13,fontWeight:700,cursor:connectedBy?'pointer':'default',whiteSpace:'nowrap'}}>
              {submitting?'Saving…':'✓ Log Connection'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function T2MManager() {
  const { get, post } = useApi();
  const { role } = useAuth();
  const { currentClub } = useClub();
  const [loading, setLoading] = useState(true);
  const [atRiskTrials, setAtRiskTrials] = useState([]);
  const [connectionNeeded, setConnectionNeeded] = useState([]);
  const [connectedTodayIds, setConnectedTodayIds] = useState(new Set());
  const [error, setError] = useState(null);
  const canEdit = role==='admin'||role==='mgr';

  const load = useCallback(async () => {
    if (!currentClub?.id) return;
    setLoading(true); setError(null);
    try {
      const data = await get(`/t2m/manager?club=${currentClub.id}`);
      setAtRiskTrials(data.atRisk||[]);
      setConnectionNeeded(data.connectionNeeded||[]);
    } catch(e) { setError('Failed to load. '+(e.message||'')); }
    finally { setLoading(false); }
  }, [currentClub?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  async function handleLogConnection(trial, connectedBy, notes) {
    try {
      await post('/t2m/manager/'+(trial.trial_id||trial.id), { connectedBy, notes, locationId: currentClub?.id });
      setConnectedTodayIds(s=>new Set([...s, trial.trial_id||trial.id]));
    } catch(e) { alert('Failed: '+(e.message||'')); }
  }

  const kpiData={
    atRisk: atRiskTrials.length,
    connectionNeeded: connectionNeeded.length,
    day9Plus: [...atRiskTrials,...connectionNeeded].filter(t=>getTrialDay(t.trial_start_date)>=9).length,
    connectedToday: connectedTodayIds.size,
    activeTrials: atRiskTrials.length+connectionNeeded.length,
  };
  const clubName=currentClub?.name||'Club';

  if (loading) return <div style={{padding:32,textAlign:'center',color:'#666'}}>Loading manager view…</div>;
  if (error) return <div style={{padding:32,textAlign:'center',color:'#ef4444'}}>{error}<br/><button onClick={load} style={{marginTop:12,background:'#facc15',color:'#000',border:'none',borderRadius:6,padding:'8px 20px',cursor:'pointer',fontWeight:700}}>Try Again</button></div>;

  return (
    <div style={{fontFamily:'DM Sans,sans-serif',maxWidth:900,margin:'0 auto',padding:'0 0 40px'}}>
      <div style={{marginBottom:16}}>
        <h2 style={{margin:0,fontSize:20,fontWeight:800,color:'#f97316',letterSpacing:'0.03em'}}>MANAGER CONNECTIONS</h2>
        <div style={{fontSize:12,color:'#888',marginTop:2}}>{clubName} · Members requiring personal manager touch · Sorted by urgency</div>
      </div>
      <div style={{background:canEdit?'#22c55e11':'#33333322',border:`1px solid ${canEdit?'#22c55e33':'#33333355'}`,borderRadius:8,padding:'10px 16px',marginBottom:20,fontSize:12,color:canEdit?'#22c55e':'#888'}}>
        {canEdit?'🤝 Manager view — You can log connections, add notes, and hand off to Sales from this tab.':'👁 View only — Contact your Club Manager or Admin to log connections.'}
      </div>
      <KPIBar data={kpiData} />
      <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
        <button onClick={load} style={{background:'transparent',color:'#888',border:'1px solid #333',borderRadius:6,padding:'5px 14px',fontSize:11,cursor:'pointer'}}>↻ Refresh</button>
      </div>
      {atRiskTrials.length>0 && (
        <div style={{marginBottom:28}}>
          <SectionHeader color="#ef4444" icon="🚨" title="At Risk — Manager Escalation Required" subtitle="Sales has attempted contact · Manager personal touch needed now" count={atRiskTrials.length} badge="Critical" />
          {atRiskTrials.map(t=><ManagerCard key={t.trial_id||t.id} trial={t} canEdit={canEdit} onLogConnection={handleLogConnection} connectedToday={connectedTodayIds.has(t.trial_id||t.id)} />)}
        </div>
      )}
      {connectionNeeded.length>0 && (
        <div style={{marginBottom:28}}>
          <SectionHeader color="#eab308" icon="🤝" title="Connection Needed" subtitle="Active trials where manager has not yet connected" count={connectionNeeded.filter(t=>!connectedTodayIds.has(t.trial_id||t.id)).length} />
          {connectionNeeded.map(t=><ManagerCard key={t.trial_id||t.id} trial={t} canEdit={canEdit} onLogConnection={handleLogConnection} connectedToday={connectedTodayIds.has(t.trial_id||t.id)} />)}
        </div>
      )}
      {atRiskTrials.length===0&&connectionNeeded.length===0&&(
        <div style={{textAlign:'center',padding:'48px 24px',color:'#555'}}>
          <div style={{fontSize:40,marginBottom:12}}>🏆</div>
          <div style={{fontSize:18,fontWeight:700,color:'#888'}}>All caught up</div>
          <div style={{fontSize:13,marginTop:6}}>No manager escalations needed for {clubName} right now.</div>
        </div>
      )}
    </div>
  );
}
